import { useSyncExternalStore } from 'react';

import type { Speech2textInfo, TranscriptionPipelineState } from '@/hooks/useTranscriptionConnection';
import { logger } from '@/lib/logger';
import { selectTabAudioStream } from '@/lib/audio/selectTabAudioStream';
import { createRecordingStartSignal, logRecordingStartEvent } from '@/lib/audio/recordingStartFeedback';

export type TranscriptionRecordingEngineState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'recording'
  | 'processing'
  | 'completed'
  | 'error';

export interface TranscriptionRecordingSessionState {
  // Engine state (XState)
  engineState: TranscriptionRecordingEngineState;

  // High-level UI flags
  isRecording: boolean;
  isProcessing: boolean;
  isStarting: boolean;
  isSelectingSource: boolean;

  // Session
  meetingId: string | null;
  recordingStartedAtMs: number | null;

  // Audio source prefs
  supportsTabAudio: boolean;
  useTabAudio: boolean;
  useMicAudio: boolean;
  selectedTabStream: MediaStream | null;
  selectedTabLabel: string | null;

  // Transcript state
  latestRealtimeTranscript: string;
  formattedTranscript: string;
  rawCommittedText: string;
  rawDraftText: string;
  rawRevision: number | null;
  pipelineState: TranscriptionPipelineState | null;
  speech2textInfo: Speech2textInfo | null;
  statusMessage: string | null;
  error: string;
  errorRetryAction: 'start' | 'source';

  // UI helpers
  activeTab: 'realtime' | 'completed';

  // Used to trigger list refresh in the page without tying it to component lifecycle.
  completedCount: number;
}

export type TranscriptionRecordingEngineControls = {
  checkPermission: () => void;
  toggle: () => Promise<void>;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /**
   * 既存の WebSocket 上で同期的に `action: 'final'` を ws.send() する。
   * pagehide のように非同期 state machine 遷移を待てない瞬間でも、
   * これを呼べばサーバ側に finalize が届いてから WS が閉じる。
   */
  requestFinal?: () => Promise<void>;
};

const initialState: TranscriptionRecordingSessionState = {
  engineState: 'idle',
  isRecording: false,
  isProcessing: false,
  isStarting: false,
  isSelectingSource: false,
  meetingId: null,
  recordingStartedAtMs: null,
  supportsTabAudio: true,
  useTabAudio: false,
  useMicAudio: true,
  selectedTabStream: null,
  selectedTabLabel: null,
  latestRealtimeTranscript: '',
  formattedTranscript: '',
  rawCommittedText: '',
  rawDraftText: '',
  rawRevision: null,
  pipelineState: null,
  speech2textInfo: null,
  statusMessage: null,
  error: '',
  errorRetryAction: 'start',
  activeTab: 'realtime',
  completedCount: 0,
};

// Match constants from RecordButton for session resume detection.
const ACTIVE_RECORDING_SESSION_KEY = 'transcription.activeRecording.v1';
const ACTIVE_RECORDING_RESUME_WINDOW_RECORDING_MS = 60_000;
const ACTIVE_RECORDING_RESUME_WINDOW_PROCESSING_MS = 30 * 60_000;

type ActiveRecordingSessionPhase = 'recording' | 'processing';

function getResumeWindowMs(phase: ActiveRecordingSessionPhase): number {
  return phase === 'processing'
    ? ACTIVE_RECORDING_RESUME_WINDOW_PROCESSING_MS
    : ACTIVE_RECORDING_RESUME_WINDOW_RECORDING_MS;
}

function clearActiveRecordingSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(ACTIVE_RECORDING_SESSION_KEY);
  } catch (error) {
    logger.warn('[useTranscriptionRecordingSession] Failed to clear active recording session', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * True while we're waiting for the RecordButton's resume effect to fire.
 * Prevents the initial onStateValueChange('idle') from overriding the
 * pre-populated 'connecting' state.
 */
let resumePending = false;

/** The same resume window governs initial UI state and audio preference recovery. */
export function getTranscriptionRecordingResumePhase(): ActiveRecordingSessionPhase | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_RECORDING_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<{ meetingId: string; updatedAt: number; phase?: ActiveRecordingSessionPhase }>;
    if (!parsed?.meetingId || !parsed?.updatedAt) return null;
    const phase = parsed.phase === 'processing' ? 'processing' : 'recording';
    return Date.now() - parsed.updatedAt <= getResumeWindowMs(phase) ? phase : null;
  } catch { return null; }
}

/** Pre-populate state from sessionStorage so the ticker shows immediately on reload. */
function resolveInitialState(): TranscriptionRecordingSessionState {
  const phase = getTranscriptionRecordingResumePhase();
  if (!phase) return initialState;
  resumePending = true;
  // sessionStorage is tab-scoped: this is the returning recording owner.
  setOwnerTab(true);
  return {
    ...initialState,
    engineState: 'connecting',
    statusMessage: phase === 'processing'
      ? '文字起こし処理を復元しています…'
      : '録音セッションを復元しています…',
  };
}

/**
 * Returns true (once) if a resume was detected at module init time.
 * The caller should skip setting engineState to 'idle' when this returns true,
 * because the RecordButton's resume effect will handle the actual transition.
 */
export function consumeResumePending(): boolean {
  if (resumePending) {
    resumePending = false;
    return true;
  }
  return false;
}

type Listener = () => void;

// 別タブで開いた詳細パネルにもライブ文字起こしを反映するため、ライブ表示に
// 必要なフィールドだけ BroadcastChannel + localStorage で同期する。録音
// エンジン (audio capture) は移譲できないので、受信側タブは「viewer」専用。
const BROADCAST_CHANNEL = 'kakusill-transcription-session-sync';
const BROADCAST_STORAGE_KEY = 'kakusill.transcriptionSession.broadcast.v1';
const BROADCASTABLE_FIELDS = [
  'engineState',
  'isRecording',
  'isProcessing',
  'meetingId',
  'recordingStartedAtMs',
  'rawCommittedText',
  'rawDraftText',
  'rawRevision',
  'latestRealtimeTranscript',
  'formattedTranscript',
  'pipelineState',
  'speech2textInfo',
  'statusMessage',
  'error',
] as const satisfies readonly (keyof TranscriptionRecordingSessionState)[];
type BroadcastableField = (typeof BROADCASTABLE_FIELDS)[number];
type BroadcastablePatch = Partial<Pick<TranscriptionRecordingSessionState, BroadcastableField>>;

let bcChannel: BroadcastChannel | null = null;
let bcInitialized = false;
let suppressBroadcast = false;

// このタブが「録音を実際に駆動している側」かどうか。
// - 自分で `startTranscriptionRecording()` を呼んだ／sessionStorage 復元で
//   resumePending が立ったタブは owner。
// - owner だけが state を BroadcastChannel に流す（viewer タブが mount 直後に
//   発火する `engineState: 'idle'` で owner 側の状態を踏み潰さないため）。
// - viewer タブから「録音停止」を押されたら、owner にだけ stop-request を投げて
//   実際の停止を肩代わりさせる。
let _ownerTabFlag = false;
function setOwnerTab(value: boolean): void {
  _ownerTabFlag = value;
}
function isOwnerTab(): boolean {
  return _ownerTabFlag;
}

type ChannelMessage =
  | { kind: 'state-update'; patch: BroadcastablePatch }
  | { kind: 'stop-request' };

const TERMINAL_ENGINE_STATES: ReadonlyArray<TranscriptionRecordingEngineState> = [
  'idle',
  'completed',
  'error',
];

function copyBroadcastableField<K extends BroadcastableField>(
  out: BroadcastablePatch,
  partial: Partial<TranscriptionRecordingSessionState>,
  field: K,
): void {
  if (field in partial) {
    out[field] = partial[field];
  }
}

function pickBroadcastable(
  partial: Partial<TranscriptionRecordingSessionState>,
): BroadcastablePatch {
  const out: BroadcastablePatch = {};
  for (const field of BROADCASTABLE_FIELDS) {
    copyBroadcastableField(out, partial, field);
  }
  return out;
}

// localStorage に残ったライブ状態は新タブの「mount 時バックログ復元」用。
// だが他タブが録音終了せずに閉じた等の理由で stale なまま残ると、誤った
// 「録音中」状態を新タブで表示してしまう。30 分の TTL で防ぐ。
const BROADCAST_HYDRATE_TTL_MS = 30 * 60_000;

interface BroadcastSnapshot {
  data: BroadcastablePatch;
  ts: number;
}

function persistBroadcastableSnapshot(): void {
  if (typeof window === 'undefined') return;
  try {
    const wrapped: BroadcastSnapshot = { data: pickBroadcastable(state), ts: Date.now() };
    window.localStorage.setItem(BROADCAST_STORAGE_KEY, JSON.stringify(wrapped));
  } catch {
    // QuotaExceeded など。失敗しても in-memory は維持。
  }
}

function hydrateBroadcastableFromStorage(): BroadcastablePatch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BROADCAST_STORAGE_KEY);
    if (!raw) return null;
    const wrapped = JSON.parse(raw) as Partial<BroadcastSnapshot>;
    if (!wrapped?.ts || !wrapped.data) return null;
    if (Date.now() - wrapped.ts > BROADCAST_HYDRATE_TTL_MS) return null;
    return wrapped.data;
  } catch {
    return null;
  }
}

function applyIncomingBroadcastPatch(incoming: BroadcastablePatch): void {
  suppressBroadcast = true;
  try {
    state = { ...state, ...incoming };
    persistBroadcastableSnapshot();
    emit();
  } finally {
    suppressBroadcast = false;
  }
}

function handleChannelMessage(message: ChannelMessage | null | undefined): void {
  if (!message) return;
  if (message.kind === 'stop-request') {
    if (isOwnerTab()) {
      void stopTranscriptionRecording();
    }
    return;
  }
  if (message.kind !== 'state-update' || !message.patch) return;
  applyIncomingBroadcastPatch(message.patch);
}

function ensureBroadcastChannel(): void {
  if (bcInitialized || typeof window === 'undefined') return;
  bcInitialized = true;

  // 別タブで進行中の録音がある場合に、新タブが mount された瞬間にバックログを表示できるよう、
  // localStorage にスナップショットされた最終ブロードキャスト状態を取り込む。
  const stored = hydrateBroadcastableFromStorage();
  if (stored && Object.keys(stored).length > 0) {
    state = { ...state, ...stored };
  }

  if (typeof BroadcastChannel === 'undefined') return;
  bcChannel = new BroadcastChannel(BROADCAST_CHANNEL);
  bcChannel.onmessage = (event: MessageEvent<ChannelMessage>) => {
    const message = event.data;
    if (!message || typeof message !== 'object') return;
    handleChannelMessage(message);
  };
}

let state: TranscriptionRecordingSessionState = resolveInitialState();

// モジュール読み込み時に localStorage から最新ブロードキャスト snapshot を取り込み、
// `useSyncExternalStore` の初回 getSnapshot で既にライブ状態を返せるようにする。
// （subscribe 経由の遅延 hydrate だと初回 render 時には空状態のままになる。）
ensureBroadcastChannel();

const listeners = new Set<Listener>();

let engineControls: TranscriptionRecordingEngineControls | null = null;

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(callback: Listener): () => void {
  ensureBroadcastChannel();
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): TranscriptionRecordingSessionState {
  return state;
}

function getServerSnapshot(): TranscriptionRecordingSessionState {
  return initialState;
}

export function useTranscriptionRecordingSession(): TranscriptionRecordingSessionState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function setTranscriptionRecordingSessionState(
  patch:
    | Partial<TranscriptionRecordingSessionState>
    | ((prev: TranscriptionRecordingSessionState) => Partial<TranscriptionRecordingSessionState>)
): void {
  ensureBroadcastChannel();
  const partial = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...partial };
  emit();
  if (suppressBroadcast) return;
  // 自分が録音を駆動している owner タブだけが他タブにブロードキャストする。
  // viewer タブの `engineState: 'idle'` 等のローカル副産物で owner の状態を
  // 上書きしないためのガード。
  if (!isOwnerTab()) return;
  const broadcastable = pickBroadcastable(partial);
  if (Object.keys(broadcastable).length === 0) return;
  persistBroadcastableSnapshot();
  const message: ChannelMessage = { kind: 'state-update', patch: broadcastable };
  bcChannel?.postMessage(message);
  // engine が terminal に落ちたら ownership を返上する（次回 start で再取得）。
  if (
    typeof partial.engineState === 'string' &&
    TERMINAL_ENGINE_STATES.includes(partial.engineState)
  ) {
    setOwnerTab(false);
  }
}

export function setTranscriptionEngineControls(
  controls: TranscriptionRecordingEngineControls | null
): void {
  engineControls = controls;
}

let startInFlight: Promise<void> | null = null;

function waitForRecordingStart(): Promise<void> {
  return new Promise((resolve, reject) => {
    const check = () => {
      if (!state.isRecording && !state.error) return;
      listeners.delete(check);
      if (state.error) reject(new Error(state.error));
      else resolve();
    };
    listeners.add(check);
    check();
  });
}

function recordingStartErrorMessage(error: unknown): string {
  if (error instanceof Error && error.name === 'NotAllowedError') {
    return '音声へのアクセスが許可されなかったか、共有先の選択がキャンセルされました。ブラウザの権限を確認して再試行してください。';
  }
  return error instanceof Error ? error.message : '録音を開始できませんでした。再試行してください。';
}

/** 入力先の設定操作。録音開始の意図がある場合だけ attemptId を渡す。 */
let sourceSelectionInFlight: Promise<void> | null = null;

async function runSourceSelection(attemptId: string): Promise<void> {
  setTranscriptionRecordingSessionState({ isSelectingSource: true, errorRetryAction: state.isStarting ? 'start' : 'source' });
  try {
    if (!engineControls) throw new Error('録音の準備ができていません。再試行してください。');
    engineControls.checkPermission();
    const selected = await selectTabAudioStream(state.selectedTabStream);
    setSelectedTabStream(selected);
    setTranscriptionRecordingSessionState({ error: '' });
    logRecordingStartEvent('source_selected', attemptId);
  } catch (error) {
    logRecordingStartEvent(error instanceof Error && error.name === 'NotAllowedError' ? 'source_cancelled' : 'source_failed', attemptId, {
      errorKind: error instanceof Error ? error.name : 'unknown',
    });
    setTranscriptionRecordingSessionState({ error: recordingStartErrorMessage(error) });
    throw error;
  } finally {
    setTranscriptionRecordingSessionState({ isSelectingSource: false });
  }
}

export function selectTranscriptionTabAudio(attemptId = crypto.randomUUID()): Promise<void> {
  if (sourceSelectionInFlight) return sourceSelectionInFlight;
  sourceSelectionInFlight = runSourceSelection(attemptId).finally(() => { sourceSelectionInFlight = null; });
  return sourceSelectionInFlight;
}

async function runRecordingStart(attemptId: string): Promise<void> {
  const signal = createRecordingStartSignal();
  setOwnerTab(true);
  setTranscriptionRecordingSessionState({ isStarting: true, error: '', errorRetryAction: 'start', engineState: 'connecting', meetingId: null, recordingStartedAtMs: null });
  logRecordingStartEvent('start_clicked', attemptId);
  try {
    if (!engineControls) throw new Error('録音の準備ができていません。再試行してください。');
    engineControls.checkPermission();
    if (state.useTabAudio && state.supportsTabAudio &&
        !state.selectedTabStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
      // getDisplayMedia はユーザー操作中に呼ぶ。接続/API を await する前に選択する。
      await selectTranscriptionTabAudio(attemptId);
    }
    await engineControls.start();
    await waitForRecordingStart();
    signal.play();
    logRecordingStartEvent('recording_started', attemptId, { meetingId: state.meetingId });
  } catch (error) {
    const message = recordingStartErrorMessage(error);
    setTranscriptionRecordingSessionState({ error: message, engineState: 'error', isRecording: false });
    signal.dispose();
    logRecordingStartEvent('start_failed', attemptId, {
      meetingId: state.meetingId,
      errorKind: error instanceof Error ? error.name : 'unknown',
    });
    throw new Error(message);
  } finally {
    setTranscriptionRecordingSessionState({ isStarting: false });
  }
}

/** 開始意図から実際の録音開始確認までを一つの試行として扱う。 */
export function startTranscriptionRecording(): Promise<void> {
  if (startInFlight) return startInFlight;
  if (state.isRecording || state.isProcessing) return Promise.resolve();
  startInFlight = runRecordingStart(crypto.randomUUID()).finally(() => { startInFlight = null; });
  return startInFlight;
}

/** Pending capture is already a stop operation, including in viewer tabs. */
export function toggleTranscriptionRecording(): Promise<void> {
  return state.isRecording || state.engineState === 'recording'
    ? stopTranscriptionRecording() : startTranscriptionRecording();
}

export async function stopTranscriptionRecording(): Promise<void> {
  // owner タブはそのまま自分の engine を止める。viewer タブから呼ばれた場合は
  // BroadcastChannel で owner に肩代わりさせる（ローカル engine は idle のため
  // 何もせずに返ると owner 側の録音は止まらない）。
  if (isOwnerTab()) {
    const cancellingStart = state.isStarting && !state.isRecording && state.engineState === 'recording';
    await engineControls?.stop?.();
    if (cancellingStart) setSelectedTabStream({ stream: null, label: null });
    return;
  }
  ensureBroadcastChannel();
  const message: ChannelMessage = { kind: 'stop-request' };
  bcChannel?.postMessage(message);
  return Promise.resolve();
}

export function clearTranscriptionRecordingResumeSession(): void {
  clearActiveRecordingSession();
}

/**
 * 自分が owner タブのときだけローカル engine を停止し、最終フェーズへ遷移
 * させる（タブ閉じ時に呼ぶ用）。viewer タブで呼ばれた場合は何もしない
 * （viewer から `stopTranscriptionRecording` を呼ぶと BroadcastChannel で
 * owner にも届いてしまい、複数タブを開いていただけで録音が止まる事故になる）。
 *
 * sessionStorage の resume marker は engine 側 (RecordButton) が
 * 状態遷移に合わせて自動でクリアするため、ここでは触らない。
 *
 * pagehide のように非同期 state machine 遷移を待てないコンテキストで
 * 呼ばれることを想定し、まず `requestFinal()` で WS に同期 send してから
 * `stop()` を呼ぶ。これで WS が閉じる前にサーバが finalize を受信できる。
 */
/**
 * ユーザが明示的に「録音停止」したあと、broadcast snapshot が stale なまま
 * 残って FAB が「録音中」表示のままになるのを救うためのリセット。
 * - ローカル state を idle にする
 * - localStorage の broadcast snapshot をクリア
 * - BroadcastChannel に reset メッセージを流して他タブも同期させる
 *
 * ownership に関わらず実行する（viewer タブから「録音停止」を押した直後は
 * owner が既に死んでいる前提）。
 */
export function resetTranscriptionRecordingStateAfterStop(): void {
  ensureBroadcastChannel();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(BROADCAST_STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
  setOwnerTab(false);
  // 他タブが UI を残し続けないよう broadcastable な reset を流す。
  // suppressBroadcast を使わずに通常の setTranscriptionRecordingSessionState
  // を経由すると owner ガードで弾かれるので、直接適用 + 直接 broadcast する。
  const resetPatch: BroadcastablePatch = {
    engineState: 'idle',
    isRecording: false,
    isProcessing: false,
    meetingId: null,
    recordingStartedAtMs: null,
    rawCommittedText: '',
    rawDraftText: '',
    rawRevision: null,
    latestRealtimeTranscript: '',
    formattedTranscript: '',
    pipelineState: null,
    speech2textInfo: null,
    statusMessage: null,
    error: '',
  };
  state = { ...state, ...resetPatch };
  emit();
  const message: ChannelMessage = { kind: 'state-update', patch: resetPatch };
  bcChannel?.postMessage(message);
}

/**
 * 明示停止後、owner タブの state machine 遷移が観測できない場合でも
 * UI を「処理中」に寄せて meeting context を維持するためのフォールバック。
 *
 * - `meetingId` は保持し、詳細画面が同一文字起こしに紐づいたままにする
 * - transcript 本文は保持し、整形完了まで表示を継続できるようにする
 * - owner / viewer どちらからでも実行できるよう、直接 broadcast する
 */
export function forceTranscriptionRecordingProcessingState(meetingId: string): void {
  if (!meetingId.trim()) return;
  ensureBroadcastChannel();
  setOwnerTab(false);
  const processingPatch: BroadcastablePatch = {
    engineState: 'processing',
    isRecording: false,
    isProcessing: true,
    meetingId,
    statusMessage: null,
    error: '',
  };
  state = {
    ...state,
    ...processingPatch,
    activeTab: 'completed',
  };
  emit();
  persistBroadcastableSnapshot();
  const message: ChannelMessage = { kind: 'state-update', patch: processingPatch };
  bcChannel?.postMessage(message);
}

export function finalizeTranscriptionRecordingIfOwner(): Promise<void> {
  if (!isOwnerTab()) return Promise.resolve();
  // 同期的な WS 送信を最優先で実施（戻り値の Promise は awaited されない想定でも OK）。
  if (engineControls?.requestFinal) {
    void engineControls.requestFinal().catch(() => {
      /* fire-and-forget; pagehide コンテキストで例外を出してもどうにもならない */
    });
  }
  return engineControls?.stop?.() ?? Promise.resolve();
}

function normalizeAudioPrefs(
  prev: Pick<
    TranscriptionRecordingSessionState,
    'supportsTabAudio' | 'useTabAudio' | 'useMicAudio'
  >,
  patch: {
    supportsTabAudio?: boolean;
    useTabAudio?: boolean;
    useMicAudio?: boolean;
  },
): Pick<
  TranscriptionRecordingSessionState,
  'supportsTabAudio' | 'useTabAudio' | 'useMicAudio'
> {
  const supportsTabAudio =
    typeof patch.supportsTabAudio === 'boolean'
      ? patch.supportsTabAudio
      : prev.supportsTabAudio;
  let useTabAudio =
    typeof patch.useTabAudio === 'boolean' ? patch.useTabAudio : prev.useTabAudio;
  let useMicAudio =
    typeof patch.useMicAudio === 'boolean' ? patch.useMicAudio : prev.useMicAudio;

  if (!supportsTabAudio) {
    useTabAudio = false;
  }
  if (!useTabAudio && !useMicAudio) {
    useMicAudio = true;
  }

  return {
    supportsTabAudio,
    useTabAudio,
    useMicAudio,
  };
}

export function setTranscriptionAudioPrefs(patch: {
  supportsTabAudio?: boolean;
  useTabAudio?: boolean;
  useMicAudio?: boolean;
}): void {
  setTranscriptionRecordingSessionState((prev) => normalizeAudioPrefs(prev, patch));
}

export function setSelectedTabStream(next: {
  stream: MediaStream | null;
  label: string | null;
}): void {
  if (next.stream === null) {
    state.selectedTabStream?.getTracks().forEach((track) => track.stop());
  }
  setTranscriptionRecordingSessionState({
    selectedTabStream: next.stream,
    selectedTabLabel: next.label,
  });
}
